import cx from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ContentType, Link } from 'src/api/contentful/contentTypes.cloverdotcom';
import { TextV2Variant, variantClassNames } from 'src/components/TextV2/TextV2';
import { FlatEntry } from 'src/services/cms/flattenContentful';
import { getUrlWithParamsOrPdpRedirect } from 'src/utils/ExistingMerchant';
import { ExternalLinkV2 } from './ExternalLinkV2';
import { InternalLinkV2 } from './InternalLinkV2';

type AnchorProps = React.AllHTMLAttributes<HTMLAnchorElement>;
type LinkV2 = FlatEntry<Link>;

export const MANAGE_COOKIES_URL = '#manage-cookies';

export const NON_LINK_URLS = [MANAGE_COOKIES_URL];

export interface ContentLinkV2Props<T extends LinkV2 = LinkV2> extends Omit<AnchorProps, 'href'>{
  alwaysRenderChildren?: boolean;
  hideTitle?: boolean;
  entry?: T;
  variant?: TextV2Variant;
}

const openCookieBanner: React.MouseEventHandler<HTMLAnchorElement> = event => {
  event.preventDefault();
  window.evidon?.notice?.showPreferencesDialog();
};

export const ContentLinkV2 = React.forwardRef<HTMLAnchorElement, ContentLinkV2Props>(({ entry, alwaysRenderChildren, variant, className, ...props }, ref) => {
  const { i18n } = useTranslation();

  const classNames = cx(variant && variantClassNames[variant], className);

  const renderExternalLink = (entry: LinkV2) => {
    const href = getUrlWithParamsOrPdpRedirect(entry);

    if (!href || !NON_LINK_URLS.includes(href)) {
      return <ExternalLinkV2 ref={ref} entry={entry} className={classNames} {...props} />;
    }

    switch (href) {
      case MANAGE_COOKIES_URL: {
        if (i18n.language !== 'en-US') return null;

        return (
          <ExternalLinkV2 ref={ref} entry={entry} className={classNames} onClick={openCookieBanner} {...props} />
        );
      }
      default:
        return null;
    }
  };

  switch (entry?.contentType) {
    case ContentType.EXTERNAL_LINK:
      return renderExternalLink(entry);
    case ContentType.INTERNAL_LINK:
      return <InternalLinkV2 ref={ref} entry={entry} className={classNames} {...props} />;
    default:
      if (!alwaysRenderChildren) return null;
      const { hideTitle, children, ...otherProps } = props;
      return <a ref={ref} className={classNames} {...otherProps}>{children}</a>;
  }
});

ContentLinkV2.displayName = 'ContentLinkV2';
